<template>
  <main class="invite-reward-detail-page">
    <AppNavBar :title="t('invite.rewardDetails')" />

    <section v-if="reward" class="reward-detail-card">
      <div class="reward-detail-card__amount" :class="amountClass">
        {{ amountText }}
      </div>
      <div class="reward-detail-card__status" :class="statusClass">
        {{ statusText }}
      </div>

      <div class="reward-detail-list">
        <div v-if="cardType" class="reward-detail-row">
          <span>{{ t("invite.rewardCardType") }}：</span>
          <div>{{ cardType }}</div>
        </div>
        <div class="reward-detail-row">
          <span>{{ t("invite.rewardType") }}：</span>
          <div>{{ rewardType }}</div>
        </div>
        <div v-if="friendNickname" class="reward-detail-row">
          <span>{{ t("invite.friendNickname") }}：</span>
          <div>{{ friendNickname }}</div>
        </div>
        <div v-if="invitationRelation" class="reward-detail-row">
          <span>{{ t("invite.invitationRelation") }}：</span>
          <div>{{ invitationRelation }}</div>
        </div>
        <div class="reward-detail-row">
          <span>{{ amountLabel }}：</span>
          <div>{{ detailAmountText }}</div>
        </div>
        <div v-if="showPercentageWeight" class="reward-detail-row">
          <span>{{ t("invite.percentageWeight") }}：</span>
          <div>{{ percentageWeight }}</div>
        </div>
        <div v-if="showTypeElevenLevelFields" class="reward-detail-row">
          <span>{{ t("invite.commissionAllocationRatio") }}：</span>
          <div>{{ allocationRatio }}</div>
        </div>
        <div v-if="showTypeElevenLevelFields" class="reward-detail-row">
          <span>{{ t("invite.commissionSettlementCycle") }}：</span>
          <div>{{ settlementCycle }}</div>
        </div>
        <div class="reward-detail-row">
          <span>{{ t("invite.commissionSettlementMethod") }}：</span>
          <div>{{ settlementMethod }}</div>
        </div>
        <div class="reward-detail-row">
          <span>{{ t("invite.commissionTriggerTime") }}：</span>
          <div>{{ triggerTime }}</div>
        </div>
        <div v-if="isRefund" class="reward-detail-row">
          <span>{{ t("invite.friendConsumptionRefundTime") }}：</span>
          <div>{{ refundTime }}</div>
        </div>
        <div v-if="isRefund" class="reward-detail-row">
          <span>{{ t("invite.commissionDeductionTime") }}：</span>
          <div>{{ deductionTime }}</div>
        </div>
        <div v-else class="reward-detail-row">
          <span>{{ t("invite.commissionSettlementTime") }}：</span>
          <div>{{ settlementTime }}</div>
        </div>
        <div class="reward-detail-row">
          <span>{{ t("invite.commissionStatus") }}：</span>
          <div>{{ commissionStatus }}</div>
        </div>
      </div>
    </section>

    <section v-else class="reward-detail-empty">
      <van-empty
        :description="t('invite.noRewardRecords')"
        :image="emptydata"
      />
    </section>
  </main>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { getRouteState } from "@/utils/route-state";
import { formatCardTimestamp } from "@/view/card/utils";
import { refRewardDetail } from "@/api/wallet";
import {
  normalizeRewardStatus,
  rewardAmountClass,
  rewardStatusClass,
  rewardStatusTextKey,
} from "@/utils/reward-status";
import emptydata from "@/assets/images/emptydata_gray.png";

const { t } = useI18n();
const route = useRoute();
const REWARD_TYPE_TEXT_KEYS = Object.freeze({
  1: "invite.cardOpeningCommission",
  2: "invite.spendingCommission",
  3: "invite.commissionRefundDeduction",
  4: "invite.reservedReward",
  10: "invite.courseCommission",
  11: "invite.globalDirectorCourseCommission",
  12: "invite.globalDirectorCardOpeningCommission",
  13: "invite.teamBonusPoolCardOpeningCommission",
  14: "invite.teamBonusPoolCourseCommission",
  15: "invite.globalDirectorTeamCardOpeningCommission",
  16: "invite.globalDirectorTeamCourseCommission",
});
const pageState = computed(() =>
  getRouteState(route, "wallet-invite-reward-detail", {}),
);
const routeReward = computed(() =>
  Object.keys(pageState.value || {}).length ? pageState.value : null,
);
const reward = ref(null);

async function loadRewardDetail() {
  const id = routeReward.value?.id || routeReward.value?.rewardId;
  if (!id) {
    reward.value = routeReward.value;
    return;
  }

  try {
    const res = (await refRewardDetail({ id })) || routeReward.value;
    console.log("refRewardDetail", res);
    reward.value = res;
  } catch (_) {
    reward.value = routeReward.value;
  }
}

onMounted(loadRewardDetail);

function pickFirstValue(keys = []) {
  for (const key of keys) {
    const value = reward.value?.[key];
    if (value === null || value === undefined) {
      continue;
    }
    const text = String(value).trim();
    if (
      text &&
      text.toLowerCase() !== "null" &&
      text.toLowerCase() !== "undefined"
    ) {
      return text;
    }
  }
  return "";
}

const amount = computed(() => Number(reward.value?.amount || 0));
const amountSymbol = computed(
  () =>
    pickFirstValue(["symbol", "coin", "assetSymbol", "tokenSymbol"]) || "USDT",
);
const detailAmountSymbol = computed(
  () =>
    pickFirstValue([
      "currency",
      "rewardCurrency",
      "settlementCurrency",
      "tradeCurrency",
    ]) || amountSymbol.value,
);
function formatAmount(symbol) {
  const rawAmount = amount.value;
  const value = Number.isFinite(rawAmount) ? Math.abs(rawAmount) : 0;
  const displayValue = Number.isInteger(value)
    ? String(value)
    : String(Number(value.toFixed(8)));
  const prefix =
    Number(reward.value?.type) === 3
      ? "-"
      : rawAmount > 0
        ? "+"
        : rawAmount < 0
          ? "-"
          : "";
  return `${prefix}${displayValue} ${symbol}`;
}
const amountText = computed(() => formatAmount(amountSymbol.value));
const detailAmountText = computed(() => formatAmount(detailAmountSymbol.value));

const normalizedStatus = computed(() => {
  const status = normalizeRewardStatus(
    reward.value?.statusKey ?? reward.value?.status,
  );
  return status === "unknown"
    ? amount.value < 0
      ? "deducted"
      : "success"
    : status;
});
const amountClass = computed(() => rewardAmountClass(normalizedStatus.value));
const statusClass = computed(() => `is-${normalizedStatus.value}`);
const isRefund = computed(() => Number(reward.value?.type) === 3);
const statusText = computed(() => {
  return t(rewardStatusTextKey(normalizedStatus.value));
});

const cardType = computed(() =>
  pickFirstValue([
    "cardType",
    "cardTypeName",
    "cardTypeMsg",
    "cardBrand",
    "cardName",
  ]),
);
const rewardType = computed(() => {
  const type = Number(reward.value?.type ?? reward.value?.rewardType);
  const textKey = REWARD_TYPE_TEXT_KEYS[type];
  return textKey ? t(textKey) : "--";
});
const friendNickname = computed(() => pickFirstValue(["friendName"]));
const invitationRelation = computed(() =>
  Number(reward.value?.refDepth) === 2 ? t("invite.indirectFriend") : "",
);
const amountLabel = computed(() =>
  Number(reward.value?.type) !== 3
    ? t("invite.rewardAmount")
    : t("invite.deductionAmount"),
);
const showPercentageWeight = computed(() =>
  [13, 14,15,16].includes(
    Number(reward.value?.type ?? reward.value?.rewardType),
  ),
);
const percentageWeight = computed(
  () => pickFirstValue(["rate"]) || "--",
);

const showTypeElevenLevelFields = computed(
  () =>
    Number(reward.value?.type ?? reward.value?.rewardType) === 11 &&
    Number(reward.value?.refDepth) === 1,
);
const allocationRatio = computed(
  () =>
    pickFirstValue(["rate"]) ||
    t("invite.commissionAllocationRatioUnavailable"),
);
const settlementCycle = computed(() =>
  formatDateOnly(pickFirstValue(["settleAt"])),
);

const settlementMethod = computed(() => {
  const type = Number(reward.value?.type ?? reward.value?.rewardType);
  if ([13, 14, 15, 16].includes(type)) {
    return t("invite.teamCommissionSettlementCycle");
  }
  return t("invite.commissionInstantSettlement");
});

const triggerTime = computed(() => formatTime(reward.value["createAt"]));
const settlementTime = computed(() => formatTime(reward.value["planAt"]));
const refundTime = computed(() =>
  formatTime(
    pickFirstValue([
      "settleAt",
      "friendConsumptionRefundTime",
      "consumptionRefundTime",
      "refundTime",
      "chargebackTime",
      "reversalTime",
    ]),
  ),
);
const deductionTime = computed(() =>
  formatTime(
    pickFirstValue([
      "claimAt",
      "commissionDeductionTime",
      "deductionTime",
      "recoverTime",
      "recoveryTime",
      "updateTime",
    ]),
  ),
);
const commissionStatus = computed(() => {
  return statusText.value;
});

function formatTime(value) {
  if (!value) {
    return "--";
  }

  const numericValue = Number(value);
  if (Number.isFinite(numericValue)) {
    return formatCardTimestamp(numericValue) || "--";
  }

  const date = new Date(String(value).replace(/-/g, "/"));
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  const pad = (input) => String(input).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function formatDateOnly(value) {
  const formatted = formatTime(value);
  return /^\d{4}-\d{2}-\d{2}/.test(formatted) ? formatted.slice(0, 10) : "--";
}
</script>

<style scoped lang="scss">
.invite-reward-detail-page {
  min-height: var(--app-viewport-height);
  padding: 14px 8px 24px;
  background: #f5f5f5;
}

.reward-detail-card,
.reward-detail-empty {
  border-radius: 18px;
  background: #ffffff;
}

.reward-detail-card {
  padding: 26px 20px 14px;
}

.reward-detail-card__amount {
  font-size: 25px;
  font-weight: 500;
  line-height: 34px;
  text-align: center;

  &.is-pending {
    color: var(--wp-color-theme);
  }

  &.is-claimable {
    color: var(--wp-reward-claimable);
  }

  &.is-success {
    color: var(--wp-reward-success);
  }

  &.is-deducted {
    color: var(--wp-reward-deducted);
  }
}

.reward-detail-card__status {
  margin-top: 1px;
  color: #999999;
  font-size: 16px;
  line-height: 23px;
  text-align: center;

  &.is-pending {
    color: var(--wp-color-theme);
  }

  &.is-claimable {
    color: var(--wp-color-brand-1);
  }

  &.is-deducted {
    color: #f11c35;
  }
}

.reward-detail-list {
  margin-top: 27px;
}

.reward-detail-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 54px;
  gap: 16px;
  border-top: 1px dashed #e5e6eb;

  &:first-child {
    border-top: 0;
  }

  > span {
    flex: none;
    color: #555555;
    font-size: 16px;
    line-height: 22px;
  }

  > div {
    min-width: 0;
    color: #111111;
    font-size: 16px;
    font-weight: 500;
    line-height: 22px;
    text-align: right;
    overflow-wrap: anywhere;
  }
}

.reward-detail-empty {
  padding: 24px 16px;
  :deep(.van-empty__image) {
    width: 100px;
    height: 100px;
    object-fit: contain;
  }
}
</style>
